// ClubGrowthWidget.jsx
// Shows club growth over time

import React, { useMemo } from 'react';
import { FaChartLine, FaArrowUp, FaArrowDown, FaMinus } from 'react-icons/fa';
import styles from './ClubGrowthWidget.module.css';

const ClubGrowthWidget = ({
  clubId,
  members,
  programs
}) => {
  // Calculate growth metrics
  const metrics = useMemo(() => {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    // Members added this month vs last month
    const membersThisMonth = (members || []).filter(m => {
      const joinDate = new Date(m.joinedAt || m.createdAt);
      return joinDate >= thisMonth;
    }).length;

    const membersLastMonth = (members || []).filter(m => {
      const joinDate = new Date(m.joinedAt || m.createdAt);
      return joinDate >= lastMonth && joinDate < thisMonth;
    }).length;

    // Calculate member growth percentage
    let memberGrowth = 0;
    if (membersLastMonth > 0) {
      memberGrowth = Math.round(((membersThisMonth - membersLastMonth) / membersLastMonth) * 100);
    } else if (membersThisMonth > 0) {
      memberGrowth = 100;
    }

    // Programs created this month
    const programsThisMonth = (programs || []).filter(p => {
      const createdDate = new Date(p.createdAt);
      return createdDate >= thisMonth;
    }).length;

    const programsLastMonth = (programs || []).filter(p => {
      const createdDate = new Date(p.createdAt);
      return createdDate >= lastMonth && createdDate < thisMonth;
    }).length;

    // Calculate program growth
    let programGrowth = 0;
    if (programsLastMonth > 0) {
      programGrowth = Math.round(((programsThisMonth - programsLastMonth) / programsLastMonth) * 100);
    } else if (programsThisMonth > 0) {
      programGrowth = 100;
    }

    // Generate simple chart data (last 6 months)
    const chartData = [];
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonthDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      
      const count = (members || []).filter(m => {
        const joinDate = new Date(m.joinedAt || m.createdAt);
        return joinDate < nextMonthDate;
      }).length;
      
      chartData.push({
        month: monthDate.toLocaleDateString('en-US', { month: 'short' }),
        count
      });
    }

    return {
      totalMembers: members?.length || 0,
      totalPrograms: programs?.length || 0,
      membersThisMonth,
      membersLastMonth,
      memberGrowth,
      programsThisMonth,
      programGrowth,
      chartData
    };
  }, [members, programs]);

  const getGrowthIndicator = (growth) => {
    if (growth > 0) return { icon: <FaArrowUp />, class: styles.positive };
    if (growth < 0) return { icon: <FaArrowDown />, class: styles.negative };
    return { icon: <FaMinus />, class: styles.neutral };
  };

  const memberIndicator = getGrowthIndicator(metrics.memberGrowth);
  const programIndicator = getGrowthIndicator(metrics.programGrowth);

  // Calculate max for chart scaling
  const maxCount = Math.max(...metrics.chartData.map(d => d.count), 1);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaChartLine className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>Club Growth</h3>
        <span className={styles.subtitle}>This Month</span>
      </div>

      <div className={styles.content}>
        {/* Growth Stats */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <span className={styles.statLabel}>New Members</span>
              <span className={`${styles.growthBadge} ${memberIndicator.class}`}>
                {memberIndicator.icon}
                {Math.abs(metrics.memberGrowth)}%
              </span>
            </div>
            <span className={styles.statValue}>{metrics.membersThisMonth}</span>
            <span className={styles.statCompare}>vs {metrics.membersLastMonth} last month</span>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <span className={styles.statLabel}>Programs Added</span>
              <span className={`${styles.growthBadge} ${programIndicator.class}`}>
                {programIndicator.icon}
                {Math.abs(metrics.programGrowth)}%
              </span>
            </div>
            <span className={styles.statValue}>{metrics.programsThisMonth}</span>
            <span className={styles.statCompare}>this month</span>
          </div>
        </div>

        {/* Membership Chart */}
        <div className={styles.chartSection}>
          <h4 className={styles.chartTitle}>Total Membership (Last 6 Months)</h4>
          <div className={styles.chartContainer}>
            <div className={styles.chartBars}>
              {metrics.chartData.map((data, index) => (
                <div key={index} className={styles.barGroup}>
                  <div className={styles.barWrapper}>
                    <div 
                      className={styles.bar}
                      style={{ height: `${(data.count / maxCount) * 100}%` }}
                    >
                      <span className={styles.barValue}>{data.count}</span>
                    </div>
                  </div>
                  <span className={styles.barLabel}>{data.month}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Total Summary */}
        <div className={styles.summaryRow}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{metrics.totalMembers}</span>
            <span className={styles.summaryLabel}>Total Members</span>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{metrics.totalPrograms}</span>
            <span className={styles.summaryLabel}>Active Programs</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubGrowthWidget;

